
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { SiteSettings } from '../types';

interface NavbarProps {
  settings: SiteSettings;
}

const Navbar: React.FC<NavbarProps> = ({ settings }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Botox', path: '/botox-antwerpen' },
    { name: 'Fillers', path: '/fillers-antwerpen' },
    { name: 'Lippen', path: '/lippen-opvullen-antwerpen' },
    { name: 'Profhilo', path: '/profhilo-antwerpen' },
    { name: 'Tarieven', path: '/tarieven' },
  ];

  const navbarStyle = {
    backgroundColor: settings.navbarColor || '#000000',
  };

  return (
    <>
      <nav 
        className="sticky top-0 z-50 w-full border-b border-white/10 backdrop-blur-md transition-all duration-300"
        style={navbarStyle}
      >
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between min-h-[80px] md:min-h-[90px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4 group z-50 relative">
            {settings.logoUrl ? (
              <img 
                src={settings.logoUrl.replace('/upload/', '/upload/f_auto,q_auto,w_400/')} 
                alt={settings.clinicName} 
                title="MD An De Cuyper"
                className="h-16 md:h-24 w-auto max-w-[200px] md:max-w-[350px] object-contain transition-all duration-500 group-hover:scale-105" 
                loading="eager"
                width="350"
                height="96"
              />
            ) : (
              <div className="flex flex-col">
                <span className="text-primary font-display text-xl tracking-tighter leading-none mb-1 uppercase font-bold">
                  {settings.clinicName}
                </span>
                <span className="text-[7px] text-white/40 uppercase tracking-[0.4em] font-black">
                  Aesthetic Medicine
                </span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 ${
                  location.pathname === link.path 
                    ? 'text-primary' 
                    : 'text-white/60 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <a 
            href={settings.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block bg-primary hover:bg-white hover:text-black text-black px-8 py-4 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all shadow-2xl shadow-primary/10"
          >
            Afspraak maken
          </a>

          {/* Mobile Menu Button - Fixed ID for targeting if needed */}
          <button 
            id="mobile-menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden z-50 text-white p-2 focus:outline-none relative"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="w-8 h-8 text-primary transition-transform duration-300" />
            ) : (
              <Menu className="w-8 h-8 transition-transform duration-300" />
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-[#0a0a0a] z-40 flex flex-col items-center justify-start pt-40 transition-all duration-300 lg:hidden ${
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        {/* Background Elements */}
        <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-[100px]"></div>

        <div className="flex flex-col items-center gap-10 relative z-10 w-full px-6 overflow-y-auto max-h-[calc(100vh-160px)]">
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-xl font-display uppercase tracking-widest transition-all duration-500 ${
                isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              } ${
                location.pathname === link.path 
                  ? 'text-primary' 
                  : 'text-white/60 hover:text-white'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              {link.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Floating Mobile CTA - Hidden when menu is open */}
      <div className={`fixed bottom-6 left-6 right-6 z-[60] lg:hidden transition-all duration-300 ${isMenuOpen ? 'translate-y-24 opacity-0' : 'translate-y-0 opacity-100'}`}>
         <a 
            href={settings.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-primary text-black py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] text-center shadow-2xl shadow-primary/20 hover:bg-white transition-all transform active:scale-95"
         >
           Afspraak maken
         </a>
      </div>
    </>
  );
};

export default Navbar;
